<script setup lang="ts">
import { shells } from '@/shells';
import FfShell from '@platform/faculties/finfac/appearance/FfShell.vue';
import type { Division, SessionView } from '@/shared/contracts';
import NavList from '@/ui/NavList.vue';
import NavIcon from '@/ui/NavIcon.vue';
import NotificationBell from '@/components/notifications/NotificationBell.vue';
import PwaBar from '@/components/PwaBar.vue';
import { useDcVariant } from '@platform/faculties/finfac/ui/runtime/variant';
import { useTheme } from '@/composables/useTheme';
import { ref, computed, watch, onMounted } from 'vue';
import { useApi, api, clearData, navigateTo, useHead, useRoute } from '@/runtime';
const { variant, toggle:toggleVariant } = useDcVariant();
const { theme, sync: syncTheme, toggle: toggleTheme } = useTheme();
onMounted(() => { syncTheme(); });
const isFf = computed(() => division.value?.appearance === 'finfac');
watch(theme, () => { if (typeof document !== 'undefined') document.documentElement.dataset.theme = theme.value; });
const { data: division } = await useApi<Division>('/api/config', { key: 'division' });
const { data: session } = await useApi<SessionView>('/api/session', { key: 'session' });
const more = ref(false);
const busy = ref(false);
const problem = ref('');
const route = useRoute();
const desktopPrimaryPaths = new Set(['/', '/people', '/tasks', '/events', '/projects', '/review', '/kudos', '/admin']);
const mobilePrimaryPaths = new Set(['/', '/people', '/tasks', '/events', '/kudos']);
const desktopPrimaryOrder = ['/', '/people', '/tasks', '/events', '/kudos', '/projects', '/review', '/admin'];
const mobilePrimaryOrder = ['/', '/people', '/tasks', '/events', '/kudos'];
const desktopPrimary = computed(() => {
  const items = session.value?.nav ?? [];
  return division.value?.shell === 'top'
    ? items.filter(item => desktopPrimaryPaths.has(item.to)).sort((a, b) => desktopPrimaryOrder.indexOf(a.to) - desktopPrimaryOrder.indexOf(b.to))
    : items;
});
const mobilePrimary = computed(() => {
  const items = session.value?.nav ?? [];
  return division.value?.shell === 'top'
    ? items.filter(item => mobilePrimaryPaths.has(item.to)).sort((a, b) => mobilePrimaryOrder.indexOf(a.to) - mobilePrimaryOrder.indexOf(b.to))
    : items.slice(0, 3);
});
const desktopMore = computed(() => division.value?.shell === 'top' ? (session.value?.nav.filter(item => !desktopPrimaryPaths.has(item.to)) ?? []) : []);
const mobileMore = computed(() => division.value?.shell === 'top' ? (session.value?.nav.filter(item => !mobilePrimaryPaths.has(item.to)) ?? []) : (session.value?.nav.slice(3) ?? []));
watch(() => route.path, () => { more.value = false; });
useHead({ titleTemplate: title => title ? `${title} — ${division.value?.name}` : division.value?.name ?? 'Портал' });
async function signOut() {
  busy.value = true;
  try { await api('/api/auth/logout', { method: 'POST', body: {} }); clearData(); await navigateTo('/login'); }
  catch { problem.value = 'Не удалось выйти. Попробуйте ещё раз.'; }
  finally { busy.value = false; }
}
</script>
<template>
  <div v-if="division && session" class="legacy-ui" :data-appearance="division.appearance" :data-variant="variant">
    <button v-if="isFf" type="button" style="position:fixed;bottom:20px;left:20px;z-index:80;padding:10px 16px;border-radius:99px;border:1px solid #8E8C93;background:#414046;color:#FAF8F2;cursor:pointer" @click="toggleVariant">Вариант {{ variant === 'A' ? 'B' : 'A' }} →</button>
    <a class="skip-link" href="#content">К содержимому</a>
    <FfShell v-if="isFf" :session="session" :division-name="division.name" :class="{ 'password-change-required': session.mustChangePassword }">
      <div v-if="division.demo" class="environment-note"><span class="status-dot" />Проверочная версия <span>Все участники и данные вымышлены</span></div>
      <PwaBar />
      <p v-if="problem" role="alert" class="error-message">{{ problem }}</p>
      <slot />
      <footer class="page-footer">{{ division.subtitle }}<span>Пространство команды</span></footer>
      <template #mobile>
        <div v-if="!session.mustChangePassword" class="mobile-bar">
          <NavList :items="mobilePrimary" label="Мобильное меню" @select="more = false" />
          <button v-if="mobileMore.length" class="nav-link press more-button" :aria-expanded="more" aria-controls="more-menu" @click="more = !more"><NavIcon name="more" /><span>Ещё</span></button>
        </div>
        <section v-if="more && !session.mustChangePassword" id="more-menu" class="more-panel" aria-label="Другие разделы">
          <div class="section-heading"><h2>Все разделы</h2><button class="text-button" @click="more = false">Закрыть</button></div>
          <NavList class="mobile-more-list" :items="mobileMore" label="Другие разделы" @select="more = false" />
        </section>
      </template>
    </FfShell>
    <component v-else :is="shells[division.shell]" :class="{ 'password-change-required': session.mustChangePassword }">
      <template #brand><RouterLink to="/" class="brand"><span class="brand-mark" aria-hidden="true">{{ division.name.slice(0, 1) }}</span><span><strong>{{ division.name }}</strong><small>{{ division.subtitle }}</small></span></RouterLink></template>
      <template #navigation>
        <NavList v-if="!session.mustChangePassword" :items="desktopPrimary" label="Основное меню" />
      </template>
      <template #account><div class="account"><span class="account-name">{{ session.user.name }}</span><button class="icon-button press" :aria-label="theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'" :title="theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'" @click="toggleTheme"><NavIcon :name="theme === 'dark' ? 'sun' : 'moon'" /></button><NotificationBell v-if="!session.mustChangePassword" /><button class="icon-button press" :disabled="busy" aria-label="Выйти" @click="signOut"><NavIcon name="logout" /></button></div></template>
      <div v-if="division.demo" class="environment-note"><span class="status-dot" />Проверочная версия <span>Все участники и данные вымышлены</span></div>
      <PwaBar />
      <p v-if="problem" role="alert" class="error-message">{{ problem }}</p>
      <slot />
      <footer class="page-footer">{{ division.subtitle }}<span>Пространство команды</span></footer>
      <template #mobile>
        <div v-if="!session.mustChangePassword" class="mobile-bar">
          <NavList :items="mobilePrimary" label="Мобильное меню" @select="more = false" />
          <button v-if="mobileMore.length" class="nav-link press more-button" :aria-expanded="more" aria-controls="more-menu" @click="more = !more"><NavIcon name="more" /><span>Ещё</span></button>
        </div>
        <section v-if="more && !session.mustChangePassword" id="more-menu" class="more-panel" aria-label="Другие разделы">
          <div class="section-heading"><h2>Все разделы</h2><button class="text-button" @click="more = false">Закрыть</button></div>
          <NavList class="mobile-more-list" :items="mobileMore" label="Другие разделы" @select="more = false" />
        </section>
      </template>
    </component>
  </div>
</template>

<style>
.legacy-ui[data-appearance=finfac][data-variant=B] .surface { border-radius:8px;box-shadow:0 3px 3px rgba(0,0,0,.12); }
.legacy-ui[data-appearance=finfac][data-variant=B] .page-heading { border-bottom:2px solid var(--ff-ink,#414046);padding-bottom:24px; }
</style>
